// L approbation du soir — en tete de la page des environnements : le passage du soir attend-il
// l approbation de David, pour quelles applications, avec quels changements. LECTURE SEULE.
//
// Cadrage : docs/prompts/MASTER-FEAT-LA-CONSOLE-MONTRE-L-APPROBATION-DU-SOIR-2026-10-29.md.
//
// LA REGLE D AFFICHAGE : « rien n attend » ne s ecrit que sur une lecture faite, avec le dernier
// passage pour temoin ; une lecture ratee se dit avec sa raison. Le seul geste est un LIEN vers la
// page du passage sur GitHub, ou se trouve le bouton d approbation — la console n approuve jamais.
//
// @gs/ui tel quel (StatusBadge).
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { AlertTriangle, CheckCircle2, ExternalLink, GitMerge, Hourglass, PowerOff, RefreshCw } from 'lucide-react';
import { StatusBadge } from '@gs/ui';
import type { StatusBadgeProps } from '@gs/ui';
import { useTranslation } from '../i18n';
import {
  usePassageDuSoir,
  type AppDuPassage,
  type MotifGithub,
  type Passage,
  type PassageDuSoir,
  type PassageEnAttente,
  type Verification,
} from '../hooks/usePassageDuSoir';

// ── Libelles : cles LITTERALES (une cle construite echappe a i18next-parser, qui la purge) ──

function libelleMotif(m: MotifGithub, t: TFunction): string {
  switch (m.code) {
    case 'non-configure': return t('admin:env.passage.motif.nonConfigure');
    case 'identite': return t('admin:env.passage.motif.identite');
    case 'coffre-refuse': return t('admin:env.passage.motif.coffreRefuse', { http: m.http ?? '—' });
    case 'acces-refuse': return t('admin:env.passage.motif.accesRefuse', { http: m.http ?? '—' });
    case 'delai': return t('admin:env.passage.motif.delai');
    case 'reponse-invalide': return t('admin:env.passage.motif.reponseInvalide');
    case 'erreur-github': return t('admin:env.passage.motif.erreurGithub', { http: m.http ?? '—' });
  }
}

function libelleVerification(v: Verification, t: TFunction): string {
  switch (v) {
    case 'reussie': return t('admin:env.passage.verification.reussie');
    case 'echouee': return t('admin:env.passage.verification.echouee');
    case 'en-cours': return t('admin:env.passage.verification.enCours');
    case 'autre': return t('admin:env.passage.verification.autre');
  }
}

const VARIANTE_VERIFICATION: Record<Verification, StatusBadgeProps['variant']> = {
  reussie: 'success',
  echouee: 'danger',
  'en-cours': 'info',
  autre: 'warning',
};

function libelleDernier(p: Passage, t: TFunction): string {
  if (p.statut === 'waiting') return t('admin:env.passage.dernierEtat.attend');
  if (p.statut !== 'completed') return t('admin:env.passage.dernierEtat.enCours');
  switch (p.conclusion) {
    case 'success': return t('admin:env.passage.dernierEtat.reussi');
    case 'failure': return t('admin:env.passage.dernierEtat.echoue');
    case 'cancelled': return t('admin:env.passage.dernierEtat.annule');
    case 'skipped': return t('admin:env.passage.dernierEtat.saute');
    default: return t('admin:env.passage.dernierEtat.autre', { conclusion: p.conclusion ?? '—' });
  }
}

type Formats = { t: TFunction; jourHeure: (iso: string) => string };

function Ligne({ icone: Icone, ton, children }: { icone: typeof Hourglass; ton: 'attente' | 'calme' | 'alerte'; children: ReactNode }) {
  const couleur = { attente: 'border-l-brand-500', calme: 'border-l-slate-300 dark:border-l-slate-600', alerte: 'border-l-amber-500 dark:border-l-amber-400' }[ton];
  const icone = { attente: 'text-brand-600 dark:text-brand-400', calme: 'text-slate-500 dark:text-slate-400', alerte: 'text-amber-600 dark:text-amber-400' }[ton];
  return (
    <div className={`flex items-start gap-3 rounded-xl border border-l-3 border-slate-200 bg-white px-3.5 py-3 shadow-xs dark:border-slate-700 dark:bg-slate-800 ${couleur}`}>
      <Icone size={18} className={`mt-0.5 shrink-0 ${icone}`} aria-hidden />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function LienGithub({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-field font-semibold text-brand-600 underline-offset-2 touch-manipulation hover:underline dark:text-brand-400"
    >
      {children}
      <ExternalLink size={14} aria-hidden />
    </a>
  );
}

function AppsDuPassage({ apps, t }: { apps: AppDuPassage[]; t: TFunction }) {
  if (apps.length === 0) return <p className="mt-2 text-meta text-slate-500 dark:text-slate-400">{t('admin:env.passage.aucuneApp')}</p>;
  return (
    <ul className="mt-2 space-y-1">
      {apps.map((a) => (
        <li key={a.nom} className="flex flex-wrap items-center justify-between gap-2 text-meta">
          <span className="text-slate-800 dark:text-slate-100">
            {a.nom}
            <span className="ml-2 text-slate-500 dark:text-slate-400">
              {a.retard === null ? t('admin:env.passage.retardInconnu') : t('admin:env.passage.retard', { n: a.retard })}
            </span>
          </span>
          <StatusBadge variant={VARIANTE_VERIFICATION[a.verification]}>{libelleVerification(a.verification, t)}</StatusBadge>
        </li>
      ))}
    </ul>
  );
}

function CarteEnAttente({ p, f }: { p: PassageEnAttente; f: Formats }) {
  const { t } = f;
  const portes = p.portes.etat === 'lu' ? p.portes.valeur : [];
  return (
    <Ligne icone={Hourglass} ton="attente">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-field font-semibold text-slate-800 dark:text-slate-100">
          {p.passage.declencheur === 'schedule'
            ? t('admin:env.passage.attendSoir', { quand: f.jourHeure(p.passage.creeA) })
            : t('admin:env.passage.attendMain', { quand: f.jourHeure(p.passage.creeA) })}
        </p>
        <LienGithub href={p.passage.lien}>{t('admin:env.passage.ouvrir')}</LienGithub>
      </div>
      <p className="mt-1 text-meta text-slate-600 dark:text-slate-300">
        {p.portes.etat === 'illisible'
          ? t('admin:env.passage.portesIllisibles', { raison: libelleMotif(p.portes.motif, t) })
          : portes.length === 0
            ? t('admin:env.passage.aucunePorte')
            : portes.map((x) => (x.environnement === 'le-passage' ? t('admin:env.passage.porteSoir') : t('admin:env.passage.porteAutre', { porte: x.environnement }))).join(' · ')}
      </p>
      <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">{t('admin:env.passage.tete', { sha: p.tete.slice(0, 9) })}</p>

      <p className="mt-3 text-meta font-semibold text-slate-600 dark:text-slate-300">{t('admin:env.passage.appsL')}</p>
      {p.apps.etat === 'illisible' ? (
        <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">{libelleMotif(p.apps.motif, t)}</p>
      ) : (
        <AppsDuPassage apps={p.apps.valeur} t={t} />
      )}

      <p className="mt-3 text-meta font-semibold text-slate-600 dark:text-slate-300">{t('admin:env.passage.changementsL')}</p>
      {p.changements.etat === 'illisible' ? (
        <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">{libelleMotif(p.changements.motif, t)}</p>
      ) : p.changements.valeur === null ? (
        <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">{t('admin:env.passage.changementsInconnus')}</p>
      ) : (
        <>
          <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">
            {t('admin:env.passage.changementsDepuis', { n: p.changements.valeur.total, sha: p.changements.valeur.depuis.slice(0, 9) })}
            {p.changements.valeur.partiel ? ` ${t('admin:env.passage.partiel')}` : ''}
          </p>
          {p.changements.valeur.fusions.length > 0 && (
            <ul className="mt-1 space-y-1">
              {p.changements.valeur.fusions.map((x) => (
                <li key={x.sha} className="flex items-start gap-2 text-meta text-slate-800 dark:text-slate-100">
                  <GitMerge size={14} className="mt-0.5 shrink-0 text-slate-400" aria-hidden />
                  <span>
                    {x.numero !== null && <span className="mr-1.5 text-slate-500 dark:text-slate-400">#{x.numero}</span>}
                    {x.titre}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <p className="mt-3 text-meta text-slate-500 dark:text-slate-400">{t('admin:env.passage.seulSurGithub')}</p>
    </Ligne>
  );
}

function Contenu({ d, f }: { d: PassageDuSoir; f: Formats }) {
  const { t } = f;
  // Sans lecture du depot, tout est illisible avec le meme motif : on le dit une fois.
  if (d.flux.etat === 'illisible') {
    return (
      <Ligne icone={AlertTriangle} ton="alerte">
        <p className="text-field font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.passage.illisibleT')}</p>
        <p className="mt-0.5 text-meta text-slate-600 dark:text-slate-300">{libelleMotif(d.flux.motif, t)}</p>
      </Ligne>
    );
  }
  const blocs: ReactNode[] = [];
  if (d.arme.etat === 'illisible') {
    blocs.push(
      <Ligne key="arme" icone={AlertTriangle} ton="alerte">
        <p className="text-meta text-slate-600 dark:text-slate-300">{t('admin:env.passage.armeIllisible', { raison: libelleMotif(d.arme.motif, t) })}</p>
      </Ligne>,
    );
  } else if (!d.arme.valeur) {
    blocs.push(
      <Ligne key="arme" icone={PowerOff} ton="calme">
        <p className="text-field font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.passage.nonArmeT')}</p>
        <p className="mt-0.5 text-meta text-slate-600 dark:text-slate-300">{t('admin:env.passage.nonArmeS')}</p>
      </Ligne>,
    );
  }
  if (!d.flux.valeur.existe) {
    blocs.push(
      <Ligne key="flux" icone={PowerOff} ton="calme">
        <p className="text-field font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.passage.sansFluxT')}</p>
        <p className="mt-0.5 text-meta text-slate-600 dark:text-slate-300">{t('admin:env.passage.sansFluxS')}</p>
      </Ligne>,
    );
    return <div className="space-y-2">{blocs}</div>;
  }
  if (d.enAttente.etat === 'illisible') {
    blocs.push(
      <Ligne key="attente" icone={AlertTriangle} ton="alerte">
        <p className="text-field font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.passage.attenteIllisibleT')}</p>
        <p className="mt-0.5 text-meta text-slate-600 dark:text-slate-300">{libelleMotif(d.enAttente.motif, t)}</p>
        <div className="mt-1"><LienGithub href={d.flux.valeur.lien}>{t('admin:env.passage.voirFlux')}</LienGithub></div>
      </Ligne>,
    );
  } else if (d.enAttente.valeur.length > 0) {
    for (const p of d.enAttente.valeur) blocs.push(<CarteEnAttente key={p.passage.id} p={p} f={f} />);
  } else {
    blocs.push(
      <Ligne key="rien" icone={CheckCircle2} ton="calme">
        <p className="text-field font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.passage.rienT')}</p>
        <p className="mt-0.5 text-meta text-slate-600 dark:text-slate-300">
          {d.dernier.etat === 'illisible'
            ? t('admin:env.passage.dernierIllisible', { raison: libelleMotif(d.dernier.motif, t) })
            : d.dernier.valeur === null
              ? t('admin:env.passage.jamais')
              : t('admin:env.passage.dernier', { quand: f.jourHeure(d.dernier.valeur.creeA), etat: libelleDernier(d.dernier.valeur, t) })}
        </p>
        <div className="mt-1">
          <LienGithub href={d.dernier.etat === 'lu' && d.dernier.valeur ? d.dernier.valeur.lien : d.flux.valeur.lien}>
            {t('admin:env.passage.voirFlux')}
          </LienGithub>
        </div>
      </Ligne>,
    );
  }
  return <div className="space-y-2">{blocs}</div>;
}

export default function ApprobationDuSoir({ jourHeure }: { jourHeure: (iso: string) => string }) {
  const { t } = useTranslation();
  const requete = usePassageDuSoir();
  return (
    <section aria-labelledby="titre-approbation">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="titre-approbation" className="text-head font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.passage.titre')}</h2>
        <p className="text-meta text-slate-500 dark:text-slate-400">{t('admin:env.passage.aide')}</p>
      </div>
      <div className="mt-3">
        {requete.isPending ? (
          <p className="inline-flex items-center gap-2 text-field text-slate-500">
            <RefreshCw size={16} className="animate-spin" aria-hidden />
            {t('admin:env.passage.chargement')}
          </p>
        ) : requete.isError || !requete.data ? (
          <Ligne icone={AlertTriangle} ton="alerte">
            <p className="text-field font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.passage.erreur')}</p>
          </Ligne>
        ) : (
          <Contenu d={requete.data} f={{ t, jourHeure }} />
        )}
      </div>
    </section>
  );
}
